import { isSymbol } from "@shared/types/utils";
import { MarginTypes, NumberOption, LanguageNameSpaces } from "@shared/types";
import {
  LeverageType,
  OverrideType,
} from "@shared/features/bots/BotForm/common/types";
import {
  BOT_MARGIN_DATA_MODIFIED_MESSAGE,
  channelValue,
  LEVERAGE_TYPE_OPTIONS,
  MARGIN_GLOBAL_SETTINGS_KEY,
} from "@shared/features/bots/BotForm/common/constants";
import { useContext, useRef } from "react";
import { MultiSymbolMarginContext } from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/MultiSymbolMargin.context";
import { MarginData } from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/types";
import { translateOptionName } from "@shared/common/i18n/optionNames";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const useMarginSelectors = (isGlobalSettings?: boolean) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    botGeneralData,
    selectedSymbols,
    setModifiedPendingValues,
    updatedMultiSymbolMarginData,
    symbolToMarginMetadata,
    hasMaxMultiplierError,
    hasLeverageValueModifiedError,
    isViewOnly,
    isFullScreen,
    getSymbolOriginalBotMultiplier,
    ...contextValues
  } = useContext(MultiSymbolMarginContext)!;
  const multiplierOptionsRef = useRef<NumberOption[]>(undefined);

  const globalSettingsValues = {
    pendingMarginType: contextValues.pendingGlobalMarginType,
    setPendingMarginType: contextValues.setPendingGlobalMarginType,
    pendingMultiplier: contextValues.pendingGlobalMultiplier,
    setPendingMultiplier: contextValues.setPendingGlobalMultiplier,
    pendingLeverageType: contextValues.pendingGlobalLeverageType,
    setPendingLeverageType: contextValues.setPendingGlobalLeverageType,
  };

  const pendingValues = isGlobalSettings ? globalSettingsValues : contextValues;
  const {
    pendingMarginType,
    setPendingMarginType,
    pendingMultiplier,
    setPendingMultiplier,
    pendingLeverageType,
    setPendingLeverageType,
  } = pendingValues;

  const getMultiplierOptions = () => {
    if (isGlobalSettings) {
      return symbolToMarginMetadata[MARGIN_GLOBAL_SETTINGS_KEY]
        .multiplierOptions;
    }
    let maxMultiplierOptions: NumberOption[] | undefined;
    let maxMultiplier: number = -1;
    selectedSymbols.forEach((symbol) => {
      const { maxMultiplier: currentMaxMultiplier, multiplierOptions } =
        symbolToMarginMetadata[symbol] || {};
      if (currentMaxMultiplier && currentMaxMultiplier > maxMultiplier) {
        maxMultiplier = currentMaxMultiplier;
        maxMultiplierOptions = multiplierOptions;
      }
    });
    return maxMultiplierOptions?.map((option) => ({
      ...option,
      name: `${option.name}x`,
    }));
  };

  multiplierOptionsRef.current = getMultiplierOptions();

  const addModifiedPendingValue = (key: keyof MarginData) => {
    setModifiedPendingValues((pendingValues) => {
      const updatedPendingValues = new Set(pendingValues);
      updatedPendingValues.add(key);
      return updatedPendingValues;
    });
  };

  const getMarginTypeOptions = () => {
    if (isGlobalSettings) {
      return symbolToMarginMetadata[MARGIN_GLOBAL_SETTINGS_KEY]
        .marginTypeOptions;
    } else if (selectedSymbols) {
      return botGeneralData?.options?.["Margin Type"]?.[0]?.[
        [...selectedSymbols][0]
      ]?.[0];
    }
  };

  const marginTypeOptions = getMarginTypeOptions();

  const getOverrideToggleProps = (isMultiplier?: boolean) => {
    const value = isMultiplier ? pendingMultiplier : pendingMarginType;
    const onOverrideToggleChange = (overrideType?: OverrideType) => {
      if (overrideType === OverrideType.CHANNEL) {
        if (isMultiplier) {
          setPendingMultiplier(channelValue);
          setPendingLeverageType(channelValue);
          addModifiedPendingValue("multiplier");
          addModifiedPendingValue("leverageType");
        } else {
          setPendingMarginType(channelValue);
          addModifiedPendingValue("marginType");
        }
      } else {
        if (isMultiplier) {
          setPendingMultiplier(multiplierOptionsRef.current?.[0]?.value);
          setPendingLeverageType(LeverageType.EXACT);
          addModifiedPendingValue("multiplier");
          addModifiedPendingValue("leverageType");
        } else {
          setPendingMarginType(marginTypeOptions?.[0]?.value);
          addModifiedPendingValue("marginType");
        }
      }
    };
    return {
      options: [OverrideType.CHANNEL, OverrideType.PERSONAL],
      selectedOption: isSymbol(value)
        ? OverrideType.CHANNEL
        : OverrideType.PERSONAL,
      style: { marginLeft: 8 },
      onClick: onOverrideToggleChange,
      setDefaultValue: false,
    };
  };

  const getSelectorValue = (key: keyof MarginData) => {
    const keyToValue = {
      marginType: "pendingMarginType",
      multiplier: "pendingMultiplier",
      leverageType: "pendingLeverageType",
    } as const;
    let pendingValue = pendingValues[keyToValue[key]];
    if (isGlobalSettings && !pendingValue) {
      pendingValue =
        updatedMultiSymbolMarginData[MARGIN_GLOBAL_SETTINGS_KEY]?.[key];
    }
    return isSymbol(pendingValue) ? undefined : pendingValue;
  };

  const getPlaceholder = (value: any) => {
    if (!value) return t("margin.multiple");
    return isSymbol(value) ? translateOptionName("Channel") : "";
  };

  const marginTypeSelectorData = {
    title: t("labels.margin_type"),
    value: getSelectorValue("marginType"),
    onChange: (marginType: MarginTypes) => {
      setPendingMarginType(marginType);
      addModifiedPendingValue("marginType");
    },
    placeholder: getPlaceholder(pendingMarginType),
    options: marginTypeOptions,
    overrideToggleProps: getOverrideToggleProps(),
  };

  const multiplierSelectorData = {
    title: t("labels.multiplier"),
    value: getSelectorValue("multiplier"),
    onChange: (multiplier: number) => {
      setPendingMultiplier(multiplier);
      addModifiedPendingValue("multiplier");
      if (isSymbol(pendingLeverageType)) {
        setPendingLeverageType(LeverageType.EXACT);
        addModifiedPendingValue("leverageType");
      }
    },
    placeholder: getPlaceholder(pendingMultiplier),
    options: multiplierOptionsRef.current,
    overrideToggleProps: getOverrideToggleProps(true),
  };

  const leverageTypeSelectorData = {
    value: getSelectorValue("leverageType"),
    onChange: (leverageType: LeverageType) => {
      setPendingLeverageType(leverageType);
      addModifiedPendingValue("leverageType");
      if (isSymbol(pendingMultiplier)) {
        setPendingMultiplier(multiplierOptionsRef.current?.[0].value);
        addModifiedPendingValue("multiplier");
      }
    },
    placeholder: getPlaceholder(pendingLeverageType),
    options: LEVERAGE_TYPE_OPTIONS,
    title: "Multiplier Type",
  };

  const getCopyLeverageModifiedError = () => {
    if (!isGlobalSettings && hasLeverageValueModifiedError)
      return BOT_MARGIN_DATA_MODIFIED_MESSAGE;
    else if (isGlobalSettings) {
      const updatedData =
        updatedMultiSymbolMarginData[MARGIN_GLOBAL_SETTINGS_KEY];
      const hasError = updatedData?.hasLeverageValueModifiedError;
      if (hasError) {
        const originalGlobalMultiplier = getSymbolOriginalBotMultiplier(
          MARGIN_GLOBAL_SETTINGS_KEY
        );
        const originalValueText =
          !originalGlobalMultiplier || isSymbol(originalGlobalMultiplier)
            ? ""
            : `(${originalGlobalMultiplier}x) `;
        return `The original bot's global settings multiplier ${originalValueText}was adjusted to the maximum that is allowed on the exchange.`;
      }
    }
  };

  return {
    marginTypeSelectorData,
    multiplierSelectorData,
    leverageTypeSelectorData,
    hasMaxMultiplierError: !isGlobalSettings && hasMaxMultiplierError,
    leverageModifiedError: getCopyLeverageModifiedError(),
    isViewOnly,
    isFullScreen,
  };
};

export default useMarginSelectors;
